import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '../views/HomeView.vue'
import { setupGlobalGuards } from './guards.js'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView,
      meta: {
        title: '首页'
      }
    },
    {
      path: '/about',
      name: 'about',
      component: () => import('../views/AboutView.vue'),
      meta: {
        title: '关于我们'
      }
    },
    {
      path: '/components',
      name: 'components',
      component: () => import('../views/ComponentsView.vue'),
      meta: {
        title: '组件展示'
      }
    },
    // 认证相关路由
    {
      path: '/auth',
      name: 'auth',
      component: () => import('../views/auth/AuthView.vue'),
      meta: {
        title: '登录注册',
        requiresGuest: true
      }
    },
    {
      path: '/login',
      redirect: '/auth'
    },
    {
      path: '/register',
      redirect: '/auth'
    },
    // 控制台布局路由 - 需要登录即可访问
    {
      path: '/dashboard',
      component: () => import('../layouts/DashboardLayout.vue'),
      meta: { 
        requiresAuth: true,
        roles: [1, 2, 3, 4] // 管理员、老师、评委、学生都可访问
      },
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('../views/dashboard/DashboardView.vue'),
          meta: { title: '控制台' }
        },

        {
          path: '/competitions',
          name: 'competitions',
          component: () => import('../views/competition/CompetitionList.vue'),
          meta: { 
            title: '赛事管理',
            roles: [1, 2, 3, 4] // 所有用户都可查看比赛列表
          }
        },
        {
          path: '/competitions/create',
          name: 'competition-create',
          component: () => import('../views/competition/CompetitionCreate.vue'),
          meta: { 
            title: '创建比赛',
            roles: [1] // 仅管理员可创建比赛
          }
        },
        {
          path: '/competitions/:id',
          name: 'competition-detail',
          component: () => import('../views/competition/CompetitionDetail.vue'),
          meta: { 
            title: '比赛详情',
            roles: [1, 2, 3, 4] // 所有用户都可查看比赛详情
          }
        },
        {
          path: '/competitions/:id/manage',
          name: 'competition-manage',
          component: () => import('../views/competition/CompetitionManage.vue'),
          meta: { title: '管理比赛' }
        },
        {
          path: '/multimodal-management',
          name: 'multimodal-management',
          component: () => import('../views/competition/MultimodalManagement.vue'),
          meta: { 
            title: '多模态数据库管理',
            roles: [1] // 仅管理员
          }
        },
        {
          path: '/competitions/:competitionId/stats-board',
          name: 'competition-stats-board',
          component: () => import('../views/competition/StatsBoard.vue'),
          meta: { title: '统计大屏', roles: [1] } // 仅管理员
        },
        {
          path: '/competitions/:id/register',
          name: 'competition-register',
          component: () => import('../views/competition/CompetitionRegister.vue'),
          meta: { 
            title: '报名参赛',
            roles: [2, 4] // 老师和学生可以报名参赛
          }
        },
        {
          path: '/competitions/:id/judges',
          name: 'competition-judges',
          component: () => import('../views/competition/CompetitionJudges.vue'),
          meta: { title: '管理评委' }
        },
        {
          path: '/competitions/:id/participants',
          name: 'competition-participants',
          component: () => import('../views/competition/CompetitionParticipants.vue'),
          meta: { title: '已参赛用户' }
        },
        {
          path: '/participants/:userId/profile',
          name: 'participant-profile',
          component: () => import('../views/competition/ParticipantProfile.vue'),
          meta: { title: '参赛用户详情' }
        },
        {
          path: '/competitions/:id/advancements',
          name: 'competition-advancements',
          component: () => import('../views/competition/CompetitionAdvancements.vue'),
          meta: { title: '晋级管理' }
        },
        {
          path: '/competitions/:id/scores',
          name: 'competition-scores',
          component: () => import('../views/competition/CompetitionScores.vue'),
          meta: { title: '评分统计' }
        },
        {
          path: '/competitions/:competitionId/upload-video',
          name: 'video-upload',
          component: () => import('../views/competition/VideoUploadClean.vue'),
          meta: { 
            title: '上传参赛视频',
            roles: [2, 4] // 老师和学生可以上传视频
          }
        },
        {
          path: '/competitions/:competitionId/video-upload-clean',
          name: 'VideoUploadClean',
          component: () => import('../views/competition/VideoUploadClean.vue'),
          meta: { 
            title: '参赛作品详情',
            roles: [1, 2, 3, 4] // 所有用户都可查看作品详情
          }
        },
        {
          path: '/competitions/:id/scoring',
          name: 'competition-scoring',
          component: () => import('../views/competition/CompetitionScoring.vue'),
          meta: { 
            title: '赛事评分',
            roles: [1, 3] // 只允许管理员和评委访问
          }
        },
        {
          path: '/competitions/:id/self-scoring',
          name: 'competition-self-scoring',
          component: () => import('../views/competition/CompetitionSelfScoring.vue'),
          meta: { 
            title: '作品自评',
            roles: [4] // 仅允许学生访问
          }
        },
        {
          path: '/debug/registration',
          name: 'registration-debug',
          component: () => import('../views/debug/RegistrationDebug.vue'),
          meta: { title: '报名状态调试' }
        },
        {
          path: '/users',
          name: 'users',
          component: () => import('../views/users/UsersView.vue'),
          meta: { title: '用户管理' }
        },
        // {
        //   path: '/scoring',
        //   name: 'scoring',
        //   component: () => import('../views/scoring/ScoringView.vue'),
        //   meta: { title: '评分系统' }
        // },
        // {
        //   path: '/reports',
        //   name: 'reports',
        //   component: () => import('../views/reports/ReportsView.vue'),
        //   meta: { title: '报告分析' }
        // },
        {
          path: '/settings',
          name: 'settings',
          component: () => import('../views/settings/SettingsView.vue'),
          meta: { 
            title: '系统设置',
            requiresAdmin: true
          }
        },
        {
          path: '/settings/profile',
          name: 'profile-settings',
          component: () => import('../views/user/ProfileView.vue'),
          meta: { 
            title: '个人设置',
            roles: [1, 2, 3, 4] // 所有用户都可访问个人设置
          }
        },
        {
          path: '/settings/skill-tags',
          name: 'skill-tags',
          component: () => import('../views/admin/SkillTagManager.vue'),
          meta: {
            title: '技能标签管理',
            requiresAdmin: true
          }
        },
        {
          path: '/admin/datasets',
          name: 'dataset-management',
          component: () => import('../views/admin/DatasetManagement.vue'),
          meta: {
            title: '数据集管理',
            requiresAdmin: true
          }
        },
        {
          path: '/admin/dataset-upload',
          name: 'dataset-upload',
          component: () => import('../views/admin/DatasetUpload.vue'),
          meta: {
            title: '数据集上传',
            requiresAdmin: true
          }
        },
        {
          path: '/admin/scoring',
          name: 'admin-scoring',
          component: () => import('../views/admin/DatasetScoring.vue'),
          meta: {
            title: '数据集在线评分',
            roles: [1, 2, 3] // 允许管理员、老师和评委访问
          }
        },
        {
          path: '/admin/my-scores',
          name: 'admin-my-scores',
          component: () => import('../views/admin/MyScores.vue'),
          meta: {
            title: '我的评分',
            roles: [1, 3] // 允许管理员和评委访问
          }
        },
        {
          path: '/admin/user/:userId',
          name: 'UserDetail',
          component: () => import('../views/admin/UserDetail.vue'),
          meta: {
            title: '用户详情',
            requiresAdmin: true
          }
        },
        {
          path: '/scoring/model',
          name: 'model-scoring',
          component: () => import('../views/scoring/ModelScoring.vue'),
          meta: {
            title: '模型评分',
            requiresAuth: true,
            roles: [1, 2, 3, 4] // 允许所有角色访问
          }
        },
        {
          path: '/contact',
          name: 'contact',
          component: () => import('../views/contact/ContactView.vue'),
          meta: {
            title: '联系我们',
            roles: [1, 2, 3, 4] // 所有用户都可访问联系我们页面
          }
        },
        {
          path: '/vision-detection',
          name: 'VisionDetection',
          component: () => import('../components/VisionDetectionComponent.vue'),
          meta: {
            title: '多模态情感分析系统',
            roles: [1] // 仅管理员可访问
          }
        },
        {
          path: '/audio-analysis',
          name: 'audio-analysis',
          component: () => import('../views/tools/AudioAnalysis.vue'),
          meta: {
            title: '合唱音准节奏分析',
            roles: [1, 2, 3]
          }
        }
        ,
        {
          path: '/reports/big-board',
          name: 'BigBoard',
          component: () => import('../views/reports/BigBoardView.vue'),
          meta: {
            title: '数据大屏',
            roles: [1, 2, 3, 4]
          }
        }
      ]
    },
    // 账户待审核页面
    {
      path: '/account-pending',
      name: 'account-pending',
      component: () => import('../views/AccountPending.vue'),
      meta: {
        title: '账户待审核'
      }
    },
    // 错误页面
    {
      path: '/403',
      name: 'forbidden',
      component: () => import('../views/error/403View.vue'),
      meta: {
        title: '权限不足'
      }
    },
    {
      path: '/404',
      name: 'notFound',
      component: () => import('../views/error/404View.vue'),
      meta: {
        title: '页面未找到'
      }
    },
    // 捕获所有未匹配的路由
    {
      path: '/:pathMatch(.*)*',
      redirect: '/404'
    }
  ]
})

// 设置全局路由守卫
setupGlobalGuards(router)

export default router
